import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { gsap } from 'gsap';
import { getColorForPosition } from './utils/colorUtils';

// Performance: Image and canvas caching with memory management
const imageCache = new Map();
const canvasCache = new Map();
const MAX_CACHE_SIZE = 50; // Limit cache size to prevent memory leaks

// Memory management utilities
const cleanupCache = () => {
  if (imageCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(imageCache.entries());
    // Remove oldest entries
    for (let i = 0; i < 10; i++) {
      imageCache.delete(entries[i][0]);
    }
  }
  if (canvasCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(canvasCache.entries());
    for (let i = 0; i < 10; i++) {
      canvasCache.delete(entries[i][0]);
    }
  }
};

// Performance: Dynamic visible range based on zoom level
const getVisibleRange = (zoomLevel = 1) => {
  if (zoomLevel > 0.5) return 5; // Close zoom: fewer cards for smooth performance
  if (zoomLevel > 0.3) return 3; // Medium zoom: minimal cards
  return 1; // Max zoom out: only center card for maximum performance
};

export default function CoverFlow({ coverflowCards, onItemClick, scaleFactor = 1.75 }) {
  const itemsRef = useRef([]);

  const numItems = 15; // Increased for deeper perspective and expansive feel
  
  // Dynamic performance: fewer rendered cards when zoomed out
  const normalizedZoom = (scaleFactor - 0.5) / (3.0 - 0.5); // Normalize to 0-1
  const visibleItems = getVisibleRange(normalizedZoom);

  itemsRef.current = [];

  // Memoized card width calculation
  const cardDimensions = useMemo(() => ({
    width: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--coverflow-card-width')),
    height: parseInt(getComputedStyle(document.documentElement).getPropertyValue('--coverflow-card-height'))
  }), []);

  // Optimized image rendering function
  const renderOptimizedImage = useCallback(async (card, canvas, dimensions) => {
    const cacheKey = `${card.id}_${dimensions.width}x${dimensions.height}`;
    
    // Check canvas cache first
    if (canvasCache.has(cacheKey)) {
      const cachedImageData = canvasCache.get(cacheKey);
      const ctx = canvas.getContext('2d');
      ctx.putImageData(cachedImageData, 0, 0);
      return;
    }

    // Check image cache
    let img = imageCache.get(card.id);
    if (!img) {
      img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = card.imageData;
      });
      
      imageCache.set(card.id, img);
    }

    // Render with performance optimizations
    const ctx = canvas.getContext('2d', { 
      alpha: false, // Disable alpha for better performance
      desynchronized: true // Allow async rendering
    });
    
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    
    // Calculate optimal scaling
    const scaleX = dimensions.width / img.naturalWidth;
    const scaleY = dimensions.height / img.naturalHeight;
    const scale = Math.max(scaleX, scaleY);
    
    const scaledWidth = img.naturalWidth * scale;
    const scaledHeight = img.naturalHeight * scale;
    const x = (dimensions.width - scaledWidth) / 2;
    const y = (dimensions.height - scaledHeight) / 2;
    
    // Use high-quality rendering for better results
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
    
    // Cache the rendered result
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      canvasCache.set(cacheKey, imageData);
      
      // Periodic cleanup to prevent memory leaks
      if (Math.random() < 0.1) { // 10% chance per render
        cleanupCache();
      }
    } catch (e) {
      // Handle potential security errors silently
    }
    
    // Prevent context menu and dragging
    canvas.oncontextmenu = (e) => e.preventDefault();
    canvas.ondragstart = (e) => e.preventDefault();
  }, []);

  useEffect(() => {
    // Performance: Use requestAnimationFrame for smooth updates
    const updateCards = async () => {
      for (let i = 0; i < itemsRef.current.length; i++) {
        const item = itemsRef.current[i];
        if (!item) continue;
      const cardData = coverflowCards[i];
      if (!cardData) return;
      
      const { card, position, relativePosition } = cardData;

      // Clear previous content
      item.innerHTML = '';

      const absPosition = Math.abs(relativePosition);
      
      // Virtual rendering: Only render cards within visible range for performance
      const shouldRender = absPosition <= visibleItems / 2;
      
      if (card && card.imageData && shouldRender) {
        // Performance: Reuse existing canvas if possible
        let canvas = item.querySelector('canvas');
        if (!canvas) {
          const imgContainer = document.createElement('div');
          imgContainer.style.width = '100%';
          imgContainer.style.height = '100%';
          imgContainer.style.position = 'relative';
          imgContainer.style.overflow = 'hidden';
          imgContainer.style.borderRadius = '24px';
          imgContainer.style.willChange = 'transform'; // GPU acceleration hint

          canvas = document.createElement('canvas');
          canvas.style.width = '100%';
          canvas.style.height = '100%';
          canvas.style.borderRadius = 'inherit';
          
          imgContainer.appendChild(canvas);
          item.appendChild(imgContainer);
        }
        
        // Use optimized rendering
        try {
          await renderOptimizedImage(card, canvas, cardDimensions);
        } catch (error) {
          console.warn('Image rendering failed:', error);
          // Fall back to color
          gsap.to(item, {
            duration: 0.8,
            backgroundColor: card.getColor(),
            ease: 'power2.out',
          });
        }
      } else if (!shouldRender && card && card.imageData) {
        // Virtual rendering: Clear non-visible cards to save memory
        item.innerHTML = '';
        gsap.set(item, { backgroundColor: 'transparent' });
      } else {
        // Fallback to color for empty positions - use shared color utility
        const color = getColorForPosition(position.x, position.y);
        
        gsap.to(item, {
          duration: 1.2,
          backgroundColor: color,
          ease: 'elastic.out(1, 0.8)',
        });
        
        // Add position indicator
        const placeholder = document.createElement('div');
        placeholder.textContent = `${position.x},${position.y}`;
        placeholder.style.color = 'white';
        placeholder.style.fontSize = '18px';
        placeholder.style.textShadow = '0 1px 2px rgba(0,0,0,0.7)';
        item.appendChild(placeholder);
      }

      // Expansive feel: Deeper perspective and more visible cards
      const xOffset = relativePosition * (cardDimensions.width * 0.40); // Tighter spacing for more cards
      const zOffset = -absPosition * 60; // Less z-distance for cards to stay more visible
      const rotationY = relativePosition * -30; // Gentler rotation for expansive feel
      const scale = Math.max(0.2, 1 - absPosition * 0.06); // Smaller falloff, cards stay larger longer
      
      // Extended fade range for 15 cards
      let opacity;
      if (absPosition === 0) {
        opacity = 1;
      } else if (absPosition <= 3) {
        opacity = 1 - (absPosition * 0.15); // Very gentle fade for nearby cards
      } else if (absPosition <= 6) {
        opacity = 0.55 - ((absPosition - 3) * 0.1); // Medium fade
      } else {
        opacity = Math.max(0.05, 0.25 - ((absPosition - 6) * 0.03)); // Distant cards still visible
      }

      item.style.zIndex = -absPosition;
      // Performance: Use transform3d for GPU acceleration
      const transformString = `translate3d(${xOffset}px, 0, ${zOffset}px) rotateY(${rotationY}deg) scale(${scale})`;

      // Aggressive performance: Skip animations when heavily zoomed out
      const isHeavyZoom = normalizedZoom < 0.3;
      
      if (isHeavyZoom) {
        // Instant positioning for maximum zoom out performance
        gsap.set(item, {
          opacity: opacity,
          transform: transformString,
          transformOrigin: '50% 50%',
          force3D: true,
        });
        
        // Simplified filters for performance
        if (absPosition === 0) {
          item.style.filter = `drop-shadow(0 0 10px rgba(0, 0, 0, 0.1))`;
        } else {
          item.style.filter = 'none';
        }
      } else {
        // Normal performance optimized animation
        gsap.to(item, {
          duration: normalizedZoom > 0.7 ? 0.6 : 0.3, // Faster when zoomed out
          ease: 'power2.out',
          opacity: opacity,
          transformOrigin: '50% 50%',
          transform: transformString,
          overwrite: 'auto',
          force3D: true,
          // Simplified filter effects for better performance
          onComplete: function() {
            if (absPosition === 0) {
              item.style.filter = `drop-shadow(0 0 10px rgba(0, 0, 0, 0.1))`;
            } else if (absPosition <= 2) {
              const blurAmount = absPosition * 0.2;
              item.style.filter = `blur(${blurAmount}px)`;
            } else {
              item.style.filter = 'blur(1px) opacity(0.7)';
            }
          }
        });
      }
      }
    };

    // Performance: Use requestAnimationFrame for smooth updates
    requestAnimationFrame(updateCards);
  }, [coverflowCards]);

  const handleClick = (i) => {
    const cardData = coverflowCards[i];
    if (cardData) {
      onItemClick(cardData.relativePosition);
    }
  };

  const coverflowItems = [];
  for (let i = 0; i < numItems; i++) {
    coverflowItems.push(
      <div
        key={i}
        className="coverflow-item"
        ref={el => itemsRef.current[i] = el}
        onClick={() => handleClick(i)}
      />
    );
  }

  return (
    <>
      {coverflowItems}
    </>
  );
}
